import React, { lazy, Suspense } from "react";
import {
  Link,
} from "react-router-dom";
import {
  AppBar,
  Toolbar,
  Typography,
  CssBaseline,
  ThemeProvider,
  createTheme,
  Box,
  useMediaQuery,
  CircularProgress,
} from "@mui/material";

import './toggleSwitch.css';
import './App.css';
import './ButtonStyle.css';
import './loding.css';

import { useAuth } from "./AuthContext";
import { TabProvider, useTabs } from "./TabContext";
import PermissionRoute from "./components/common/PermissionRoute";
import HorizontalNavbar from "./HorizontalNavbar";
import MobileBottomNav from "./MobileBottomNav";
import TabBar from "./TabBar";
import Logout from "./Logout";
import PWAStatus from "./components/common/PWAStatus";
import SessionTimeout from "./SessionTimeout";
import BpsLogo from "./components/pages/Logo/BpsGlobal.jpeg";

// ---- Lazy loaded pages ----
const Home = lazy(() => import("./components/pages/Home"));
const RoleIndex = lazy(() => import("./components/pages/Role/Index"));
const RoleDetail = lazy(() => import("./components/pages/Role/Detail"));
const UserPermissionIndex = lazy(() => import("./components/pages/UserPermission/UserPermissionIndex"));
const UserIndex = lazy(() => import("./components/pages/User/Index"));
const UserCreate = lazy(() => import("./components/pages/User/Create"));
const UserEdit = lazy(() => import("./components/pages/User/Edit"));
const UserDetail = lazy(() => import("./components/pages/User/Detail"));
const PasswordChange = lazy(() => import("./components/pages/User/PasswordChange"));
const ProductCategoryIndex = lazy(() => import("./components/pages/ProductCategory/Index"));
const ProductItemIndex = lazy(() => import("./components/pages/ProductItem/Index"));
const ProductBrandIndex = lazy(() => import("./components/pages/ProductBrand/Index"));
const ProductRelationIndex = lazy(() => import("./components/pages/ProductRelation/Index"));
const CurrencyTypeIndex = lazy(() => import("./components/pages/CurrencyType/CurrencyTypeIndex"));
const TermsAndConditionsIndex = lazy(() => import("./components/pages/TermsAndConditions/Index"));
const BankIndex = lazy(() => import("./components/pages/Bank/Index"));
const MasterDashboard = lazy(() => import("./components/pages/Master/MasterDashboard"));
const ClientIndex = lazy(() => import("./components/pages/Master/ClientIndex"));
const VendorIndex = lazy(() => import("./components/pages/Master/VendorIndex"));
const AccountHeadDashboard = lazy(() => import("./components/pages/AccountHead/AccountHeadDashboard"));
const AccountHeadIndex = lazy(() => import("./components/pages/AccountHead/AccountHeadIndex"));
const AssetIndex = lazy(() => import("./components/pages/AccountHead/AssetIndex"));
const CapitalIndex = lazy(() => import("./components/pages/AccountHead/CapitalIndex"));
const OtherOptionIndex = lazy(() => import("./components/pages/AccountHead/OtherOptionIndex"));
const BalanceSheetHeadIndex = lazy(() => import("./components/pages/AccountHead/BalanceSheetHeadIndex"));
const PurchaseOrderIndex = lazy(() => import("./components/pages/PurchaseOrder/PurchaseOrderIndex"));
const PurchaseIndex = lazy(() => import("./components/pages/Purchase/PurchaseIndex"));
const PurchaseReturnAgainstIndex = lazy(() => import("./components/pages/PurchaseReturn/PurchaseReturnAgainstIndex"));
const SaleReturnAgainstIndex = lazy(() => import("./components/pages/SaleReturn/SaleReturnAgainstIndex"));
const QuotationIndex = lazy(() => import("./components/pages/Quotation/QuotationIndex"));
const SalesOrderIndex = lazy(() => import("./components/pages/SaleOrder/SalesOrderIndex"));
const SaleIndex = lazy(() => import("./components/pages/Sale/SaleIndex"));
const StockTransferInIndex = lazy(() => import("./components/pages/Stock/StockTransferInIndex"));
const StockTransferOutIndex = lazy(() => import("./components/pages/Stock/StockTransferOutIndex"));
const StockJournalIndex = lazy(() => import("./components/pages/Stock/StockJournalIndex"));
const PaymentReceiptsIndex = lazy(() => import("./components/pages/PaymentReceipts/PaymentReceiptsIndex"));
const JournalEntryIndex = lazy(() => import("./components/pages/JournalEntry/JournalEntryIndex"));
const PettyCashIndex = lazy(() => import("./components/pages/PettyCash/PettyCashIndex"));
const TrialBalanceReport = lazy(() => import("./components/pages/Report/TrialBalanceReport"));
const TrialBalanceAdvancedReport = lazy(() => import("./components/pages/Report/TrialBalanceAdvancedReport"));

const LedgerReport = lazy(() => import("./components/pages/Report/LedgerReport"));
const VendorDueListReport = lazy(() => import("./components/pages/Report/VendorDueListReport"));
const CustomerDueListReport = lazy(() => import("./components/pages/Report/CustomerDueListReport"));
const OutstandingReport = lazy(() => import("./components/pages/Report/OutstandingReport"));
const VendorAgingReport = lazy(() => import("./components/pages/Report/VendorAgingReport"));
const CustomerAgingReport = lazy(() => import("./components/pages/Report/CustomerAgingReport"));
const PurchasePartyandItemWiseReport = lazy(() => import("./components/pages/Report/PurchasePartyandItemWiseReport"));
const SalePartyandItemWiseReport = lazy(() => import("./components/pages/Report/SalePartyandItemWiseReport"));
const CashBook = lazy(() => import("./components/pages/Report/CashBook"));
const StockReport = lazy(() => import("./components/pages/Report/StockReport"));
const DetailedInvoiceReport = lazy(() => import("./components/pages/Report/DetailedInvoice"));
const IncomeExpenseSummaryReport = lazy(() => import("./components/pages/Report/IncomeExpenseSummaryReport"));
const PettyCashReport = lazy(() => import("./components/pages/Report/PettyCashReports"));
const ProfitAndLossReport = lazy(() => import("./components/pages/Report/ProfitAndLossReport"));
const BalanceSheetReport = lazy(() => import("./components/pages/Report/BalanceSheetReport"));
const CompanyEdit = lazy(() => import("./components/pages/Company/Edit"));
const StaffDashboard = lazy(() => import("./components/pages/Staff/StaffDashboard"));
const DepartmentIndex = lazy(() => import("./components/pages/Department/DepartmentIndex"));
const DesignationIndex = lazy(() => import("./components/pages/Designation/DesignationIndex"));

// ---- Theme (base — tab pages use their own scoped theme) ----
const theme = createTheme({
  palette: {
    primary: {
      main: "#6366f1",
      dark: "#4f46e5",
      light: "#e0e7ff",
      contrastText: "#ffffff",
    },
    secondary: {
      main: "#10b981",
      dark: "#047857",
      light: "#d1fae5",
      contrastText: "#ffffff",
    },
    background: {
      default: "#f8fafc",
      paper: "#ffffff",
    },
  },
  typography: {
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: "8px",
          textTransform: "none",
          fontWeight: 600,
          transition: "all 0.2s ease-in-out",
          "&:hover": { transform: "translateY(-1px)" },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: { root: { boxShadow: "none" } },
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          "& .MuiInputBase-root": { minHeight: "28px", paddingTop: "0 !important", paddingBottom: "0 !important" },
          "& .MuiOutlinedInput-root": { minHeight: "28px", padding: "0 9px !important" },
          "& .MuiAutocomplete-input": { padding: "3.5px 4px !important", fontSize: "0.8rem" },
          "& .MuiInputBase-input": { fontSize: "0.8rem" },
          "& .MuiAutocomplete-endAdornment": { top: "50%", transform: "translateY(-50%)" },
        },
        listbox: {
          fontSize: "0.8rem",
          maxHeight: "200px",
          "& .MuiAutocomplete-option": {
            fontSize: "0.8rem",
            minHeight: "32px",
            padding: "4px 12px",
          },
        },
        paper: {
          boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
          borderRadius: "6px",
          border: "1px solid #e5e7eb",
        },
        noOptions: { fontSize: "0.8rem", padding: "8px 12px" },
        loading: { fontSize: "0.8rem", padding: "8px 12px" },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          fontSize: "0.8rem",
          minHeight: "unset",
          padding: "3.5px 8px !important",
        },
        icon: { color: "#6b7280" },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: "0.8rem",
          minHeight: "32px",
          padding: "4px 12px",
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          "&.MuiInputBase-sizeSmall": {
            fontSize: "0.8rem",
          },
        },
        input: {
          "&.MuiInputBase-inputSizeSmall": {
            fontSize: "0.8rem",
            padding: "3.5px 8px",
          },
        },
      },
    },
  },
});

// ---- Page loading spinner ----
const PageLoader = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
    <CircularProgress size={36} sx={{ color: '#0f3460' }} />
  </Box>
);

// Route → component + permission map for tab rendering
const TAB_ROUTES = [
  { path: '/Home', component: Home, permission: null },
  { path: '/RoleIndex', component: RoleIndex, permission: ["Read Administration"] },
  { path: '/RoleDetail', component: RoleDetail, permission: ["Read Administration"] },
  { path: '/UserPermissionIndex', component: UserPermissionIndex, permission: ["Read Administration"] },
  { path: '/UserIndex', component: UserIndex, permission: ["Read Administration"] },
  { path: '/UserCreate', component: UserCreate, permission: ["Create Administration"] },
  { path: '/UserEdit', component: UserEdit, permission: ["Edit Administration"] },
  { path: '/UserDetail', component: UserDetail, permission: ["Read Administration"] },
  { path: '/PasswordChange', component: PasswordChange, permission: ["Edit Administration"] },
  { path: '/ProductCategoryIndex', component: ProductCategoryIndex, permission: ["Read Master"] },
  { path: '/ProductItemIndex', component: ProductItemIndex, permission: ["Read Master"] },
  { path: '/ProductBrandIndex', component: ProductBrandIndex, permission: ["Read Master"] },
  { path: '/ProductRelationIndex', component: ProductRelationIndex, permission: ["Read Master"] },
  { path: '/CurrencyTypeIndex', component: CurrencyTypeIndex, permission: ["Read Master"] },
  { path: '/TermsAndConditionsIndex', component: TermsAndConditionsIndex, permission: ["Read Master"] },
  { path: '/BankIndex', component: BankIndex, permission: ["Read Master"] },
  { path: '/MasterDashboard', component: MasterDashboard, permission: ["Read Master"] },
  { path: '/ClientIndex', component: ClientIndex, permission: ["Read Master"] },
  { path: '/VendorIndex', component: VendorIndex, permission: ["Read Master"] },
  { path: '/AccountHeadDashboard', component: AccountHeadDashboard, permission: ["Read Master"] },
  { path: '/AccountHeadIndex', component: AccountHeadIndex, permission: ["Read Master"] },
  { path: '/AssetIndex', component: AssetIndex, permission: ["Read Master"] },
  { path: '/CapitalIndex', component: CapitalIndex, permission: ["Read Master"] },
  { path: '/OtherOptionIndex', component: OtherOptionIndex, permission: ["Read Master"] },
  { path: '/BalanceSheetHeadIndex', component: BalanceSheetHeadIndex, permission: ["Read Master"] },
  { path: '/PurchaseOrderIndex', component: PurchaseOrderIndex, permission: ["Read Transaction"] },
  { path: '/PurchaseIndex', component: PurchaseIndex, permission: ["Read Transaction"] },
  { path: '/PurchaseReturnAgainstIndex', component: PurchaseReturnAgainstIndex, permission: ["Read Transaction"] },
  { path: '/QuotationIndex', component: QuotationIndex, permission: ["Read Transaction"] },
  { path: '/SaleOrderIndex', component: SalesOrderIndex, permission: ["Read Transaction"] },
  { path: '/SaleIndex', component: SaleIndex, permission: ["Read Transaction"] },
  { path: '/SaleReturnAgainstIndex', component: SaleReturnAgainstIndex, permission: ["Read Transaction"] },
  { path: '/StockTransferInIndex', component: StockTransferInIndex, permission: ["Read Transaction"] },
  { path: '/StockTransferOutIndex', component: StockTransferOutIndex, permission: ["Read Transaction"] },
  { path: '/StockJournalIndex', component: StockJournalIndex, permission: ["Read Transaction"] },
  { path: '/PaymentAndReceiptIndex', component: PaymentReceiptsIndex, permission: ["Read Accounts"] },
  { path: '/JournalEntryIndex', component: JournalEntryIndex, permission: ["Read Accounts"] },
  { path: '/PettyCashIndex', component: PettyCashIndex, permission: ["Read Accounts"] },
  { path: '/TrialBalanceReport', component: TrialBalanceReport, permission: ["Read Reports"] },
  { path: '/TrialBalanceAdvancedReport', component: TrialBalanceAdvancedReport, permission: ["Read Reports"] },

  { path: '/CashBook', component: CashBook, permission: ["Read Reports"] },
  { path: '/Ledger', component: LedgerReport, permission: ["Read Reports"] },
  { path: '/VendorDueList', component: VendorDueListReport, permission: ["Read Reports"] },
  { path: '/CustomerDueList', component: CustomerDueListReport, permission: ["Read Reports"] },
  { path: '/Outstanding', component: OutstandingReport, permission: ["Read Reports"] },
  { path: '/VendorAging', component: VendorAgingReport, permission: ["Read Reports"] },
  { path: '/CustomerAging', component: CustomerAgingReport, permission: ["Read Reports"] },
  { path: '/PurchasePartyandItemWiseReport', component: PurchasePartyandItemWiseReport, permission: ["Read Reports"] },
  { path: '/SalePartyandItemWiseReport', component: SalePartyandItemWiseReport, permission: ["Read Reports"] },
  { path: '/StockReport', component: StockReport, permission: ["Read Reports"] },
  { path: '/DetailedInvoiceReport', component: DetailedInvoiceReport, permission: ["Read Reports"] },
  { path: '/IncomeExpenseSummary', component: IncomeExpenseSummaryReport, permission: ["Read Reports"] },
  { path: '/PettyCashReport', component: PettyCashReport, permission: ["Read Reports"] },
  { path: '/ProfitAndLoss', component: ProfitAndLossReport, permission: ["Read Reports"] },
  { path: '/BalanceSheet', component: BalanceSheetReport, permission: ["Read Reports"] },
  { path: '/CompanyEdit', component: CompanyEdit, permission: ["Read Administration"] },
  { path: '/StaffDashboard', component: StaffDashboard, permission: ["Read Administration"] },
  { path: '/DepartmentIndex', component: DepartmentIndex, permission: ["Read Master"] },
  { path: '/DesignationIndex', component: DesignationIndex, permission: ["Read Master"] },
];

// Each tab gets its own modal container div. MUI Dialogs/Modals on that tab
// portal into it. When tab is inactive, container is hidden → all its modals hidden.
const TabPage = ({ tab, isActive }) => {
  const containerRef = React.useRef(null);
  const [containerEl, setContainerEl] = React.useState(null);

  const tabTheme = React.useMemo(() => createTheme({
    zIndex: { modal: 1450, drawer: 1450, snackbar: 1460, tooltip: 1500 },
    palette: {
      primary: { main: "#6366f1", dark: "#4f46e5", light: "#e0e7ff", contrastText: "#ffffff" },
      secondary: { main: "#10b981", dark: "#047857", light: "#d1fae5", contrastText: "#ffffff" },
      background: { default: "#f8fafc", paper: "#ffffff" },
    },
    typography: { fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" },
    components: {
      MuiDialog: {
        defaultProps: { container: () => containerEl },
        styleOverrides: {
          root: {
            top: '80px !important',
          },
          paper: {
            maxHeight: 'calc(100vh - 96px)',
          },
        },
      },
      MuiModal: {
        defaultProps: { container: () => containerEl },
        styleOverrides: {
          root: {
            top: '80px !important',
          },
        },
      },
      MuiSelect: {
        defaultProps: {
          MenuProps: { disablePortal: true },
        },
      },
      MuiAutocomplete: {
        defaultProps: {
          disablePortal: true,
        },
      },
      MuiBackdrop: {
        styleOverrides: {
          root: {
            top: '80px !important',
          },
        },
      },
    },
  }), [containerEl]);

  const route = TAB_ROUTES.find(r => {
    if (r.path.toLowerCase() === tab.path.toLowerCase()) return true;
    const baseTab = '/' + tab.path.split('/')[1];
    return r.path.toLowerCase() === baseTab.toLowerCase();
  });

  return (
    <>
      {/* Portal target — hidden when tab inactive so all modals disappear with it */}
      <div
        ref={node => { containerRef.current = node; if (node !== containerEl) setContainerEl(node); }}
        style={isActive ? { position: 'relative', zIndex: 1 } : { display: 'none' }}
      />
      <div style={isActive ? {} : { display: 'none' }}>
        {route && (
          <ThemeProvider theme={tabTheme}>
            <Suspense fallback={<PageLoader />}>
              <route.component />
            </Suspense>
          </ThemeProvider>
        )}
      </div>
    </>
  );
};

const TabPages = () => {
  const { tabs, activeTab } = useTabs();
  return (
    <>
      {tabs.map(tab => (
        <TabPage key={tab.path} tab={tab} isActive={tab.path === activeTab} />
      ))}
    </>
  );
};

// ---- Layout ----
const Layout = () => {
  const { companyName, roleName, username, isAuthenticated } = useAuth();
  const isMobile = useMediaQuery('(max-width:768px)');

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <TabProvider>
          {isAuthenticated && <SessionTimeout />}

          <PWAStatus
            showInstallPrompt={true}
            showUpdatePrompt={true}
            showOfflineStatus={true}
          />

          <AppBar
            position="fixed"
            sx={{
              zIndex: 1400,
              background: "rgba(15, 23, 42, 0.95)",
              backdropFilter: "blur(12px)",
              borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
              overflow: "visible",
            }}
          >
            <Toolbar sx={{ minHeight: '48px !important', height: '48px', px: 2, overflow: 'visible' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <img
                  src={BpsLogo}
                  alt="BPS Logo"
                  style={{
                    height: '34px',
                    width: 'auto',
                    objectFit: 'contain',
                    borderRadius: '50px',
                    border: '1.5px solid rgba(255, 255, 255, 0.2)',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.15)'
                  }}
                />
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px" }}
                >
                  <Link to="/Home" style={{ textDecoration: "none", color: "inherit" }}>
                    <span style={{ fontFamily: "'Inter', sans-serif", fontSize: '13px' }}>{companyName}</span>
                  </Link>
                </Typography>
              </Box>

              {!isMobile && username !== "Counter" && <HorizontalNavbar />}
              <Box sx={{ flexGrow: 1 }} />
              <Logout />
            </Toolbar>
          </AppBar>

          {isAuthenticated && <TabBar />}

          <Box sx={{ pt: '80px', pb: isMobile ? '70px' : 0, minHeight: '100vh', backgroundColor: '#f8fafc' }}>
            <Box sx={{ p: 0 }}>
              <TabPages />
            </Box>
          </Box>

          {isMobile && username !== "Counter" && <MobileBottomNav />}
        </TabProvider>
    </ThemeProvider>
  );
};

// ---- Root ----
const App = () => {
  return <Layout />;
};

export { Layout };
export default App;
